import React from "react";
import {useNavigate} from "react-router-dom";
import {Star, Heart, ShoppingCart, Check} from "lucide-react";
import {formatNaira} from "./landingData";
import {formatCount, formatDuration} from "./publicCourseMap";
import {useWishlist} from "./useWishlist";
import {useLandingCartSync} from "./useLandingCart";

const RATING_STARS = [0, 1, 2, 3, 4];

function Stars({rating}) {
  return (
    <span className="flex items-center gap-[1px]" aria-hidden="true">
      {RATING_STARS.map((i) => (
        <Star
          key={i}
          className="h-3 w-3"
          style={{
            fill: i < Math.round(rating) ? "#f59e0b" : "none",
            color: i < Math.round(rating) ? "#f59e0b" : "#d1d5db",
          }}
        />
      ))}
    </span>
  );
}

/**
 * Reusable marketplace course card used across every discovery section.
 * `variant`: "grid" (default, fills its column) or "rail" (fixed width).
 */
export default function CourseCard({course, variant = "grid", className = "", dense = false, railWidth = "w-[268px]"}) {
  const navigate = useNavigate();
  const {has, toggle} = useWishlist();
  const {isInCart, addCourse} = useLandingCartSync();

  // Seed local UI state from the API's viewer flags (server-side cart/wishlist
  // for signed-in learners) so the card agrees with the course page.
  const wished = has(course.id) || !!course.viewer?.isWishlisted;
  const inCart = isInCart(course.id) || !!course.viewer?.isInCart;
  const openCourse = () => navigate(`/courses/${course.slug || course.id}`);

  // The card is shared with the static landing rails, so accept both the
  // public-catalog mapping and the legacy static course shape.
  const ratingAverage =
    course.ratingAverage !== undefined
      ? course.ratingAverage
      : typeof course.rating === "number"
        ? course.rating
        : null;
  const reviewCount =
    course.reviewCount !== undefined ? Number(course.reviewCount) || 0 : Number(course.reviews) || 0;
  const durationLabel =
    course.durationLabel !== undefined
      ? course.durationLabel
      : course.hours
        ? formatDuration(course.hours * 3600)
        : "";
  const personNames = (course.resourcePersons || [])
    .map((person) => person?.name)
    .filter(Boolean);
  const instructorNames = personNames.length ? personNames.join(", ") : course.instructor || "";
  const instructorCount = personNames.length || (instructorNames ? 1 : 0);

  return (
    <article
      role="link"
      tabIndex={0}
      onClick={openCourse}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openCourse();
        }
      }}
      className={`group flex h-full cursor-pointer flex-col overflow-hidden rounded-sm border border-slate-200 bg-white font-nunito shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-[0_20px_45px_-20px_rgba(15,23,42,0.28)] ${
        variant === "rail" ? `${railWidth} shrink-0` : "w-full"
      } ${className}`}
    >
      {/* Thumbnail */}
      <div
        className={`relative w-full overflow-hidden bg-slate-100 ${
          dense ? "aspect-[2/1]" : "aspect-[16/10]"
        }`}
      >
        {course.image ? (
          <img
            src={course.image}
            alt={course.title}
            loading="lazy"
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        ) : null}

        <div className="absolute left-0 top-0 flex flex-wrap gap-1">
          {course.bestseller && (
            <span className="rounded-none bg-white px-2 py-0.5 text-[11px] font-semibold text-blue-700 shadow-sm">
              Bestseller
            </span>
          )}
          {course.hot && (
            <span className="rounded-none bg-white px-2 py-0.5 text-[11px] font-semibold text-red-600 shadow-sm">
              Hot
            </span>
          )}
        </div>

        <button
          type="button"
          aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
          aria-pressed={wished}
          onClick={(event) => {
            event.stopPropagation();
            toggle(course.id);
          }}
          className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border backdrop-blur transition-all active:scale-90 ${
            wished
              ? "border-rose-200 bg-rose-50 text-rose-500"
              : "border-white/70 bg-white/85 text-slate-400 hover:text-rose-500"
          }`}
        >
          <Heart className="h-4 w-4" style={{fill: wished ? "#f43f5e" : "none"}} />
        </button>
      </div>

      {/* Body — light, Udemy-style content */}
      <div className={`flex flex-1 flex-col ${dense ? "gap-1 p-3" : "gap-1.5 p-4"}`}>
        <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug text-slate-800 transition-colors group-hover:text-blue-800">
          {course.title}
        </h3>

        {/* Rating + duration, one clean line — slots stay even when empty
            so they fill in as soon as the API returns reviews/duration. */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs font-normal text-slate-500">
          {ratingAverage !== null && (
            <span className="font-semibold text-amber-700">{ratingAverage.toFixed(1)}</span>
          )}
          <Stars rating={ratingAverage ?? 0} />
          <span>({formatCount(reviewCount)})</span>
          <span className="text-slate-300">·</span>
          <span>{durationLabel || "0 hours"}</span>
          {course.level ? (
            <span className="basis-full text-xs font-normal text-slate-500">{course.level}</span>
          ) : null}
        </div>

        <p className="line-clamp-1 text-xs font-normal text-slate-500">
          {instructorNames ? (
            <>
              {instructorCount > 1 ? "Instructors" : "Instructor"}:{" "}
              <span className="text-slate-600">{instructorNames}</span>
            </>
          ) : (
            <span className="text-slate-400">Instructor to be announced</span>
          )}
        </p>

        {/* Price + Add to cart */}
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <div className="flex items-baseline gap-2">
            <span className="text-base font-bold text-slate-900">
              {course.isFree ? "Free" : formatNaira(course.price)}
            </span>
            {!course.isFree && course.oldPrice > course.price && (
              <span className="text-xs text-slate-400 line-through">
                {formatNaira(course.oldPrice)}
              </span>
            )}
          </div>

          {!course.isFree ? (
            <button
              type="button"
              disabled={inCart}
              aria-label={inCart ? "Already in cart" : `Add ${course.title} to cart`}
              onClick={(event) => {
                // The whole card navigates to the course page — keep the add action local.
                event.stopPropagation();
                addCourse(course);
              }}
              className={`flex shrink-0 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-colors ${
                inCart
                  ? "cursor-default border border-blue-200 bg-blue-50 text-blue-800"
                  : "bg-blue-900 text-white hover:bg-blue-800 active:bg-blue-950"
              }`}
            >
              {inCart ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  In cart
                </>
              ) : (
                <>
                  <ShoppingCart className="h-3.5 w-3.5" />
                  Add to cart
                </>
              )}
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}
